import { useReducer } from "react";
import { Block, ContentItem, ContentType } from "@org/ui";
import { v4 as uuidv4 } from "uuid";

// Block state management using a reducer
export type BlockState = Block[];

export type BlockAction =
  | { type: "SET_BLOCKS"; payload: Block[] }
  | { type: "ADD_BLOCK"; payload: Block }
  | { type: "UPDATE_BLOCK"; payload: Block }
  | { type: "DELETE_BLOCK"; payload: string }
  | { type: "REORDER_BLOCKS"; payload: Block[] }
  | { type: "ADD_CONTENT"; blockId: string; content: ContentItem }
  | { type: "UPDATE_CONTENT"; blockId: string; content: ContentItem }
  | { type: "DELETE_CONTENT"; blockId: string; contentId: string }
  | { type: "REORDER_CONTENT"; blockId: string; contents: ContentItem[] }
  | { type: "UNDO_DELETE_BLOCK"; payload: string }
  | { type: "UNDO_DELETE_CONTENT"; blockId: string; contentId: string }
  | {
      type: "MOVE_CONTENT";
      fromBlockId: string;
      toBlockId: string;
      contentId: string;
      insertIndex?: number;
    }
  | {
      type: "SPLIT_BLOCK";
      blockId: string;
      splitAfterContentId: string;
      newBlock: Block;
    };

const normalizeContentOrder = (contents: ContentItem[]) =>
  contents.map((content, index) => ({
    ...content,
    order: index,
  }));

const normalizeBlockOrder = (blocks: Block[]) =>
  blocks.map((block, index) => ({
    ...block,
    order: index,
  }));

const cloneContentData = <T>(data: T): T => {
  try {
    return JSON.parse(JSON.stringify(data));
  } catch {
    return data;
  }
};

const sanitizeExerciseData = (data: any) => {
  if (!data) return data;
  const sanitized = cloneContentData(data);
  delete sanitized.exerciseId;

  if (Array.isArray(sanitized.options)) {
    sanitized.options = sanitized.options.map((option: any) => ({
      ...option,
      optionId: undefined,
      multipleChoiceOptionId: undefined,
      id: undefined,
    }));
  }

  if (Array.isArray(sanitized.sentences)) {
    sanitized.sentences = sanitized.sentences.map((sentence: any) => ({
      ...sentence,
      sentenceId: undefined,
      id: undefined,
      blanks: Array.isArray(sentence?.blanks)
        ? sentence.blanks.map((blank: any) => ({
            ...blank,
            blankId: undefined,
            id: undefined,
            options: Array.isArray(blank?.options)
              ? blank.options.map((option: any) => ({
                  ...option,
                  optionId: undefined,
                  id: undefined,
                }))
              : [],
          }))
        : [],
    }));
  }

  return sanitized;
};

const cloneContentForNewBlock = (content: ContentItem): ContentItem => {
  const clonedData = cloneContentData(content.data);
  const baseClone: ContentItem = {
    ...content,
    id: uuidv4(),
    deleted: false,
    data: clonedData,
  };

  if (content.type === ContentType.Exercise) {
    baseClone.data = sanitizeExerciseData(clonedData);
  }

  return baseClone;
};

export const blocksReducer = (state: BlockState, action: BlockAction): BlockState => {
  switch (action.type) {
    case "SET_BLOCKS":
      return action.payload;
    case "ADD_BLOCK":
      return [...state, action.payload];
    case "UPDATE_BLOCK":
      return state.map(block => (block.id === action.payload.id ? action.payload : block));
    case "DELETE_BLOCK":
      return state.map(block =>
        block.id === action.payload
          ? { ...block, deleted: true }
          : block
      );
    case "REORDER_BLOCKS":
      return action.payload;
    case "ADD_CONTENT":
      return state.map(block =>
        block.id === action.blockId ? { ...block, contents: [...block.contents, action.content] } : block
      );
    case "UPDATE_CONTENT":
      return state.map(block =>
        block.id === action.blockId
          ? { ...block, contents: block.contents.map(content => (content.id === action.content.id ? action.content : content)) }
          : block
      );
    case "DELETE_CONTENT":
      return state.map(block =>
        block.id === action.blockId
          ? { ...block, contents: block.contents.map(content => (content.id === action.contentId ? { ...content, deleted: true } : content)) }
          : block
      );
    case "REORDER_CONTENT":
      return state.map(block =>
        block.id === action.blockId ? { ...block, contents: action.contents } : block
      );
    case "UNDO_DELETE_BLOCK":
      return state.map(block =>
        block.id === action.payload ? { ...block, deleted: false } : block
      );
    case "UNDO_DELETE_CONTENT":
      return state.map(block =>
        block.id === action.blockId
          ? { ...block, contents: block.contents.map(content => (content.id === action.contentId ? { ...content, deleted: false } : content)) }
          : block
      );
    case "MOVE_CONTENT": {
      const { fromBlockId, toBlockId, contentId, insertIndex } = action;
      if (fromBlockId === toBlockId) {
        const targetBlock = state.find(block => block.id === fromBlockId);
        if (!targetBlock) return state;
        const currentIndex = targetBlock.contents.findIndex(content => content.id === contentId);
        if (currentIndex < 0) return state;

        const nextIndex = insertIndex ?? targetBlock.contents.length - 1;
        const reordered = [...targetBlock.contents];
        const [moved] = reordered.splice(currentIndex, 1);
        const safeIndex = Math.min(Math.max(nextIndex, 0), reordered.length);
        reordered.splice(safeIndex, 0, moved);

        return state.map(block =>
          block.id === fromBlockId ? { ...block, contents: normalizeContentOrder(reordered) } : block
        );
      }

      const fromBlock = state.find(block => block.id === fromBlockId);
      const toBlock = state.find(block => block.id === toBlockId);
      if (!fromBlock || !toBlock) return state;

      const contentIndex = fromBlock.contents.findIndex(content => content.id === contentId);
      if (contentIndex < 0) return state;

      const contentToMove = fromBlock.contents[contentIndex];
      if (!contentToMove) return state;
      const clonedContent = cloneContentForNewBlock(contentToMove);
      const updatedFromContents = normalizeContentOrder(
        fromBlock.contents.filter(content => content.id !== contentId)
      );
      const targetIndex =
        insertIndex === undefined
          ? toBlock.contents.length
          : Math.min(Math.max(insertIndex, 0), toBlock.contents.length);

      const updatedToContents = normalizeContentOrder([
        ...toBlock.contents.slice(0, targetIndex),
        clonedContent,
        ...toBlock.contents.slice(targetIndex),
      ]);

      return state.map(block => {
        if (block.id === fromBlockId) {
          return { ...block, contents: updatedFromContents };
        }
        if (block.id === toBlockId) {
          return { ...block, contents: updatedToContents };
        }
        return block;
      });
    }
    case "SPLIT_BLOCK": {
      const { blockId, splitAfterContentId, newBlock } = action;
      const blockIndex = state.findIndex(block => block.id === blockId);
      if (blockIndex < 0) return state;

      const block = state[blockIndex];
      const splitIndex = block.contents.findIndex(content => content.id === splitAfterContentId);
      if (splitIndex < 0 || splitIndex >= block.contents.length - 1) {
        return state;
      }

      const headContents = normalizeContentOrder(block.contents.slice(0, splitIndex + 1));
      const tailContents = normalizeContentOrder(
        block.contents.slice(splitIndex + 1).map(content => cloneContentForNewBlock(content))
      );
      const updatedBlock = { ...block, contents: headContents };
      const insertedBlock: Block = {
        ...newBlock,
        contents: tailContents,
      };

      const nextBlocks = [...state];
      nextBlocks.splice(blockIndex + 1, 0, insertedBlock);
      nextBlocks[blockIndex] = updatedBlock;

      return normalizeBlockOrder(nextBlocks);
    }
    default:
      return state;
  }
};

export const useBlocksReducer = (initialBlocks: Block[] = []) => {
  return useReducer(blocksReducer, initialBlocks);
};